import { inject, Injectable } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MultiselectItem } from '@ui/aos-lib';

import { FilterFieldMapping, ReportGroupsFilterOptions } from '../report-groups-filter-criteria.types';
import { MultiselectDynamicFilterComponent } from './dynamic-filters/multiselect-dynamic-filter.component';
import { EqualsOpFilter, FieldName, FilterType, InOpFilter } from './report-groups-dynamic-filter.const';
import {
  CreateFilterOptions,
  DynamicFilterOptions,
  ReportGroupsDynamicFilter,
  ReportGroupsDynamicFilterControl
} from './report-groups-dynamic-filter.types';
import { TextDynamicFilterComponent } from './dynamic-filters/text-dynamic-filter.component';
import { RadiobuttonDynamicFilterComponent } from './dynamic-filters/radiobutton-dynamic-filter.component';
import { CheckboxDynamicFilterComponent } from './dynamic-filters/checkbox-dynamic-filter.component';
import { LassoDynamicFilterComponent } from './dynamic-filters/lasso-dynamic-filter.component ';
import { atLeastOneCheckboxSelectedValidator } from './at-least-one-checkbox-selected.validator';

@Injectable({ providedIn: 'root' })
export class ReportGroupsFilterFactoryService {
  readonly #fb = inject(FormBuilder);

  public createMultiselectFilter({
    fieldName,
    operatorItems,
    defaultOperatorItems,
    valueItems,
    selectedValueItems
  }: {
    fieldName: string;
    operatorItems: MultiselectItem[];
    defaultOperatorItems: MultiselectItem;
    valueItems: MultiselectItem[];
    selectedValueItems: MultiselectItem[];
  }): ReportGroupsDynamicFilter {
    return {
      fieldName,
      component: MultiselectDynamicFilterComponent,
      controls: [
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.operator,
          ctrl: this.#fb.nonNullable.control(defaultOperatorItems),
          overrideDropdownListSelected: {
            enabled: true,
            inputName: 'selectedOperatorItem'
          }
        }),
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.values,
          ctrl: this.#fb.nonNullable.control(selectedValueItems),
          overrideDropdownListSelected: {
            enabled: true,
            inputName: 'selectedValueItems'
          }
        }),
        (): ReportGroupsDynamicFilterControl<ReportGroupsFilterOptions> => ({
          name: FieldName.options,
          ctrl: this.#fb.nonNullable.control<ReportGroupsFilterOptions>({
            filterType: FilterType.Multiselect
          })
        })
      ],
      inputs: {
        operatorItems,
        defaultOperatorItems,
        selectedOperatorItem: defaultOperatorItems,
        valueItems,
        selectedValueItems
      },
      createControlValueFromApiPayload: values => [
        valueItems.filter(valueItem => (values as string[]).includes(valueItem.value as string))
      ],
      createApiPayloadFromControlValue: values => (values as MultiselectItem[]).map(item => item.value as string)
    };
  }

  public createTextFilter({
    fieldName,
    operatorItems,
    defaultOperatorItems,
    createApiPayloadFromControlValue
  }: {
    fieldName: string;
    operatorItems: MultiselectItem[];
    defaultOperatorItems: MultiselectItem;
    createApiPayloadFromControlValue?: ReportGroupsDynamicFilter['createApiPayloadFromControlValue'];
  }): ReportGroupsDynamicFilter {
    return {
      fieldName,
      component: TextDynamicFilterComponent,
      controls: [
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.operator,
          ctrl: this.#fb.nonNullable.control(defaultOperatorItems),
          overrideDropdownListSelected: {
            enabled: true,
            inputName: 'selectedOperatorItem'
          }
        }),
        (): ReportGroupsDynamicFilterControl => ({ name: FieldName.values, ctrl: this.#fb.nonNullable.control('') })
      ],
      inputs: {
        operatorItems,
        defaultOperatorItems,
        selectedOperatorItem: defaultOperatorItems
      },
      createApiPayloadFromControlValue:
        createApiPayloadFromControlValue ??
        ((values: string, operator: string): string[] =>
          operator === 'IN' ? (Array.isArray(values) ? values : values.split(',').map(v => v.trim())) : [values])
    };
  }

  public createRadiobuttonFilter({ fieldName, items, defaultItem }: CreateFilterOptions): ReportGroupsDynamicFilter {
    return {
      fieldName,
      component: RadiobuttonDynamicFilterComponent,
      controls: [
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.values,
          ctrl: this.#fb.nonNullable.control(defaultItem)
        }),
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.operator,
          ctrl: this.#fb.nonNullable.control(EqualsOpFilter)
        })
      ],
      inputs: {
        items
      }
    };
  }

  public createCheckboxFilter({ fieldName, items, defaultItem }: CreateFilterOptions): ReportGroupsDynamicFilter {
    return {
      fieldName,
      component: CheckboxDynamicFilterComponent,
      controls: [
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.values,
          ctrl: this.#fb.nonNullable.array(
            [
              ...items.map(name =>
                this.#fb.nonNullable.group({
                  [name]: Array.isArray(defaultItem) ? defaultItem.includes(name) : name === defaultItem
                })
              )
            ],
            atLeastOneCheckboxSelectedValidator()
          )
        }),
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.operator,
          ctrl: this.#fb.nonNullable.control(InOpFilter)
        })
      ],
      inputs: {
        items
      }
    };
  }

  public createCommodityRadiobuttonFilter(): ReportGroupsDynamicFilter {
    return {
      fieldName: FilterFieldMapping.Commodity.fieldName as string,
      component: RadiobuttonDynamicFilterComponent,
      controls: [
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.values,
          ctrl: this.#fb.nonNullable.control(DynamicFilterOptions.Electricity)
        }),
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.operator,
          ctrl: this.#fb.nonNullable.control(EqualsOpFilter)
        }),
        (): ReportGroupsDynamicFilterControl<ReportGroupsFilterOptions> => ({
          name: FieldName.options,
          ctrl: this.#fb.nonNullable.control<ReportGroupsFilterOptions>({
            filterType: 'Radiobutton',
            removeButtonDisabled: false,
            removeButtonTooltip: 'The filter by Commodity is required when Consumption Analytics is selected'
          })
        })
      ],
      inputs: {
        items: [DynamicFilterOptions.Electricity, DynamicFilterOptions.Gas, DynamicFilterOptions.Water]
      }
    };
  }

  public createLassoFilter(): ReportGroupsDynamicFilter {
    return {
      fieldName: FilterFieldMapping.DeviceUID.fieldName as string,
      component: LassoDynamicFilterComponent,
      controls: [
        (): ReportGroupsDynamicFilterControl => ({
          name: FieldName.operator,
          ctrl: this.#fb.nonNullable.control(InOpFilter)
        }),
        (): ReportGroupsDynamicFilterControl => ({ name: FieldName.values, ctrl: this.#fb.nonNullable.control([]) })
      ],
      inputs: {},
      createControlValueFromApiPayload: values => [values] as string[][],
      createApiPayloadFromControlValue: values => values as string[]
    };
  }
}
